import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";

const SESSION_KEY = "zenfresh-splash-shown";

/**
 * A brief branded flourish shown once per browser session, layered on top
 * of (not replacing) index.html's #boot-skeleton — that skeleton covers the
 * gap before React mounts at all and can't be session-gated (it's static
 * HTML); this covers the short moment just after mount with the actual
 * ZenFresh mark, and never replays on internal SPA navigation since it's
 * mounted once at the App root, not per-route.
 */
export default function SplashScreen() {
  const reducedMotion = usePrefersReducedMotion();
  // Read synchronously on first render (not in an effect) so a reduced-
  // motion or already-seen session never paints the overlay for even one
  // frame before disappearing.
  const [visible, setVisible] = useState(() => {
    if (reducedMotion) return false;
    return sessionStorage.getItem(SESSION_KEY) !== "true";
  });

  useEffect(() => {
    if (!visible) return;
    // Set immediately, not after the timer — a mid-splash page refresh
    // should not replay it a second time.
    sessionStorage.setItem(SESSION_KEY, "true");
    const timer = setTimeout(() => setVisible(false), 700);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          aria-hidden="true"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ocean-950"
        >
          <motion.img
            src="/logo.png"
            alt=""
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="h-16 w-16 object-contain"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
